import { useMemo, useState } from 'react';
import DOMPurify from 'dompurify';

// 공시 본문 뷰 — DART 공시는 보통 HTML 포맷으로 수집되어 `disclosure_body` 에 저장됨.
// 기본 2-tab:
//   · "HTML 렌더" (기본): DOMPurify sanitize 후 dangerouslySetInnerHTML 로 렌더
//   · "HTML 원문":       raw 텍스트 <pre> 로 escape 표시 (마크업 감사용)
// 본문이 HTML 이 아닌 평문이면 sanitize 결과도 동일한 텍스트 — 별 탭 차이 없음.

interface Props {
  body: string;
  /** 본문 높이 상한. 기본 540px. */
  maxHeight?: number;
}

type Tab = 'rendered' | 'raw';

const PURIFY_CONFIG = {
  // 기본 allowlist 에서 위험 요소 제거 + 외부 링크/리소스 차단.
  FORBID_TAGS: ['script', 'style', 'iframe', 'frame', 'object', 'embed', 'link', 'meta'],
  FORBID_ATTR: ['onclick', 'onerror', 'onload', 'onmouseover', 'href', 'src', 'action', 'formaction'],
  ALLOW_DATA_ATTR: false,
};

function looksLikeHtml(text: string): boolean {
  const trimmed = text.trim().slice(0, 512).toLowerCase();
  return (
    trimmed.startsWith('<!doctype') ||
    trimmed.startsWith('<html') ||
    /<(table|p|div|span|br|tr|td|h[1-6]|body|head)\b/i.test(trimmed)
  );
}

export function DisclosureBodyView({ body, maxHeight = 540 }: Props): JSX.Element {
  const [tab, setTab] = useState<Tab>('rendered');
  const isHtml = useMemo(() => looksLikeHtml(body), [body]);
  const sanitized = useMemo(
    () => (isHtml ? DOMPurify.sanitize(body, PURIFY_CONFIG) : ''),
    [body, isHtml],
  );

  return (
    <div className="disclosure-body-view">
      <div className="disclosure-body-view__tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'rendered'}
          className={`disclosure-body-view__tab${
            tab === 'rendered' ? ' disclosure-body-view__tab--active' : ''
          }`}
          onClick={() => setTab('rendered')}
        >
          HTML 렌더
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'raw'}
          className={`disclosure-body-view__tab${
            tab === 'raw' ? ' disclosure-body-view__tab--active' : ''
          }`}
          onClick={() => setTab('raw')}
        >
          HTML 원문
        </button>
        {!isHtml && (
          <span className="disclosure-body-view__hint">
            (HTML 마크업이 아닌 평문입니다)
          </span>
        )}
      </div>

      {tab === 'rendered' ? (
        isHtml ? (
          <div
            className="disclosure-body-view__rendered"
            style={{ maxHeight }}
            // DOMPurify sanitize 완료 — XSS 안전. PURIFY_CONFIG 가 script/style/외부 리소스 차단.
            dangerouslySetInnerHTML={{ __html: sanitized }}
          />
        ) : (
          <pre
            className="disclosure-body-view__plain"
            style={{ maxHeight }}
          >
            {body}
          </pre>
        )
      ) : (
        <pre
          className="disclosure-body-view__raw"
          style={{ maxHeight }}
        >
          {body}
        </pre>
      )}
    </div>
  );
}
